import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import {
  createAutomationRule,
  getAutomationById,
  updateAutomationRule,
} from "./services/AutomationServices";
import { IconDeviceFloppy, IconArrowLeft } from "@tabler/icons-react";
import AdvancedConditions from "./AdvancedConditions";
import styled from "styled-components";
import UserContext from "../../contexts/UserContext";
import { getTemplates } from "../../services/TemplatesServices";
import NotificationsContext from "../../contexts/NotificationsContext";
import { useParams, useNavigate } from "react-router-dom";

// Schema de validação
const schema = yup
  .object({
    template_action: yup.string().required("O tipo de mensagem é obrigatório"),
    time_value: yup
      .number()
      .required("O valor do tempo é obrigatório")
      .min(1, "Valor mínimo é 1"),
    time_unit: yup.string().required("A unidade de tempo é obrigatória"),
    direction: yup.string().required("A direção é obrigatória"),
    reference: yup.string().required("A referência é obrigatória"),
  })
  .required();

const AutomationRuleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const [loading, setLoading] = useState(false);
  const [automationData, setAutomationData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // const [conditions, setConditions] = useState();

  // const handleConditionsChange = (newConditions) => {
  //   setConditions(newConditions);
  // };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      template_action: "confirmation_message",
      template: "",
      time_value: 1,
      time_unit: "days",
      direction: "before",
      reference: "appointment_start",
      priority: 1,
      active: true,
    },
  });

  const selectedAction = watch("template_action");

  // Busca dados da automação e templates disponíveis
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const automationResponse = await getAutomationById(id);
        setAutomationData(automationResponse);

        // Define os templates disponíveis
        const availableTemplates = automationResponse.available_templates || [];
        setTemplates(availableTemplates);

        // Se não tem templates na resposta, busca separadamente
        if (!automationResponse.available_templates) {
          try {
            const templatesData = await getTemplates();
            setTemplates(templatesData.results || []);
            availableTemplates = templatesData.results || [];
          } catch (templatesError) {
            console.error("Erro ao buscar templates:", templatesError);
          }
        }

        // Se existe regra, preenche o formulário com os dados da primeira regra
        if (automationResponse.rules && automationResponse.rules.length > 0) {
          const rule = automationResponse.rules[0];

          // Define o template selecionado ANTES de fazer o reset
          const templateFromList = availableTemplates.find(
            (t) => t.id === rule.template
          );

          let selectedTemplateData = null;

          if (templateFromList) {
            selectedTemplateData = {
              id: rule.template,
              name: rule.template_name || templateFromList.name,
              action: rule.template_action,
              message: templateFromList.message,
              ...templateFromList,
              // Sobrescreve com os detalhes específicos da regra se existirem
              ...(rule.template_details || {}),
            };
          } else if (rule.template_details) {
            // Fallback para os detalhes do template da regra
            selectedTemplateData = {
              id: rule.template,
              name: rule.template_name,
              action: rule.template_action,
              ...rule.template_details,
            };
          }

          // Define o template selecionado
          if (selectedTemplateData) {
            setSelectedTemplate(selectedTemplateData);
          }

          // Preenche os dados no formulário DEPOIS de definir o template
          reset({
            id: rule?.id || null,
            template_action: rule.template_action,
            template: rule.template.toString(), // Converte para string para corresponder ao value do option
            time_value: rule.time_value,
            time_unit: rule.time_unit,
            direction: rule.direction,
            reference: rule.reference,
            priority: rule.priority || 1,
            active: rule.active !== undefined ? rule.active : true,
          });
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        setNotify({
          title: "Oops!",
          message:
            "Não foi possível buscar os dados da automação! Por favor, tente novamente em alguns minutos, se o problema persistir, contacte nosso suporte.",
          type: "error",
          visible: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchData();
    } else {
      // Se for nova automação, apenas busca os templates
      const getTemplatesList = async () => {
        try {
          const templatesData = await getTemplates();
          setTemplates(templatesData.results || []);
        } catch (error) {
          console.error("Erro ao buscar templates:", error);
          setNotify({
            title: "Atenção",
            message: "Não foi possível carregar a lista de templates.",
            type: "warning",
            visible: true,
          });
        } finally {
          setIsLoading(false);
        }
      };
      getTemplatesList();
    }
  }, [id, reset, setNotify]);

  // Reinicia o template selecionado quando a ação muda
  useEffect(() => {
    setValue("template", "");
    setSelectedTemplate(null);
  }, [selectedAction, setValue]);

  // Efeito adicional para garantir que o valor do template seja definido corretamente
  useEffect(() => {
    if (selectedTemplate && templates.length > 0) {
      const templateExists = templates.find(
        (t) => t.id === selectedTemplate.id
      );
      if (templateExists) {
        setValue("template", selectedTemplate.id.toString());
      }
    }
  }, [selectedTemplate, templates, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);

    if (!selectedTemplate && data.template) {
      const found = templates.find((t) => t.id === parseInt(data.template));
      if (found) {
        setSelectedTemplate(found);
      } else {
        setNotify({
          title: "Atenção!",
          message: "Selecione um template para continuar com sua ação!",
          type: "warning",
          visible: true,
        });
        setLoading(false);
        return;
      }
    }

    try {
      const formData = {
        ...data,
        // conditions: conditions,
        user: user.user_id,
        account_id: user.account_id,
        template_id:
          data.template_id || (selectedTemplate && selectedTemplate.id),
        template_action:
          data.template_action || (selectedTemplate && selectedTemplate.action),
      };

      if (formData?.id) {
        await updateAutomationRule(id, formData.id, formData);
        Swal.fire({
          title: "Sucesso!",
          text: "Regra atualizada com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await createAutomationRule(id, formData);
        Swal.fire({
          title: "Sucesso!",
          text: "Regra criada com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
      navigate("/automations");
    } catch (error) {
      console.error("Erro ao salvar:", error);
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao salvar a regra.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/automations");
  };

  if (isLoading) {
    return (
      <ContainerWrapper className="main-container">
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </div>
      </ContainerWrapper>
    );
  }

  return (
    <ContainerWrapper className="main-container">
      <Card style={{height: 'unset'}}>
        <Card.Body>
          <Row className="mb-4">
            <Col
              xs={12}
              className="d-flex justify-content-between align-items-center"
            >
              <div>
                <h3>
                  {id
                    ? `Editar Regra: ${automationData?.name || ""}`
                    : "Nova Regra"}
                </h3>
                <p className="text-muted">
                  {id
                    ? "Atualize as configurações da regra de automação."
                    : "Defina quando enviar mensagens automaticamente."}
                </p>
              </div>
              {automationData && (
                <Badge
                  bg={automationData.active ? "success" : "secondary"}
                  className="fs-6"
                >
                  {automationData.active ? "Ativo" : "Inativo"}
                </Badge>
              )}
            </Col>
          </Row>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Mensagem</Form.Label>
                  <Form.Select
                    {...register("template_action")}
                    isInvalid={!!errors.template_action}
                  >
                    <option value="confirmation_message">Confirmação</option>
                    <option value="reminder_message">Lembrete</option>
                    <option value="feedback">Feedback</option>
                    <option value="tips_message">Dicas</option>
                    <option value="generic">Genérico</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.template_action?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Template</Form.Label>
                  <Form.Select
                    {...register("template")}
                    disabled={templates.length === 0}
                    onChange={(e) => {
                      const selected = templates.find(
                        (t) => t.id === parseInt(e.target.value)
                      );

                      if (selected) {
                        setSelectedTemplate(selected);
                      }
                    }}
                  >
                    <option value="">Selecione um template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {templates.length === 0
                      ? "Não há templates disponíveis para este tipo de mensagem. O texto padrão será usado."
                      : "Selecione um template personalizado ou use o padrão."}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Valor do Tempo</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    {...register("time_value")}
                    isInvalid={!!errors.time_value}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.time_value?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Unidade de Tempo</Form.Label>
                  <Form.Select
                    {...register("time_unit")}
                    isInvalid={!!errors.time_unit}
                  >
                    <option value="minutes">Minutos</option>
                    <option value="hours">Horas</option>
                    <option value="days">Dias</option>
                    <option value="weeks">Semanas</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.time_unit?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-1">
                  <Form.Label>Quando Enviar</Form.Label>
                  <Form.Select
                    {...register("direction")}
                    isInvalid={!!errors.direction}
                  >
                    <option value="before">Antes</option>
                    <option value="after">Depois</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.direction?.message}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    {selectedAction === "feedback"
                      ? "Para mensagens de feedback, a referência é o final do evento."
                      : "A referência é o início do evento."}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-1">
                  <Form.Label>Referência</Form.Label>
                  <Form.Select
                    {...register("reference")}
                    isInvalid={!!errors.reference}
                  >
                    <option value="appointment_start">
                      Início do agendamento
                    </option>
                    <option value="appointment_created_at">
                      Criação do agendamento
                    </option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.reference?.message}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Referência é a data de início ou criação do agendamento.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3} className="ml-3">
                <Form.Group className="mb-1">
                  <Form.Check
                    type="checkbox"
                    id="active-switch"
                    label="Regra Ativa"
                    {...register("active")}
                  />
                  <Form.Text className="text-muted">
                    Se desativada, esta regra específica não gerará mensagens.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {selectedTemplate && (
              <Row className="mt-3 mb-4 bg-light rounded p-4">
                <Col md={12}>
                  <h5>Preview do Template</h5>
                  <div>
                    <strong>Nome:</strong> {selectedTemplate.name}
                    <br />
                    <strong>Mensagem:</strong>{" "}
                    {selectedTemplate.message?.text || "Sem texto definido"}
                    {selectedTemplate.media_url && (
                      <div>
                        <strong>Mídia:</strong> {selectedTemplate.media_type}
                      </div>
                    )}
                    {selectedTemplate.document_url && (
                      <div>
                        <strong>Documento:</strong>{" "}
                        {selectedTemplate.document_name}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            )}

            <Row className="mt-4">
              <Col sm={12} md={4}>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  className="d-flex align-items-center"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <IconDeviceFloppy size={18} className="me-1" />
                      Salvar
                    </>
                  )}
                </Button>
              </Col>
            </Row>
            {/*   <Row>
              <Col md={12}>
                <AdvancedConditions
                  conditions={conditions}
                  onChange={handleConditionsChange}
                />
              </Col>
            </Row> */}
          </Form>
        </Card.Body>
      </Card>
    </ContainerWrapper>
  );
};

const ContainerWrapper = styled.div`
  margin: auto;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

export default AutomationRuleForm;
